/**
 * Mobile Operator Login Button
 * Pulsante globale per generare QR code temporaneo per login operatori
 * Con pre-selezione operatore e memorizzazione ultimo usato
 */

import { useState, useEffect } from 'react';
import { QrCode, Loader2, X, User, Clock, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { useSharedWebSocket } from '../../hooks/useSharedWebSocket';
import { QRCodeSVG } from 'qrcode.react';

// generate-login-token richiede il JWT dal 2026-08-03 (vedi mobile-operators-auth.js).
const authHeader = (): Record<string, string> => {
  try { const t = localStorage.getItem('accessToken'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; }
};

interface MobileOperatorLoginButtonProps {
  className?: string;
}

interface Operator {
  id: number;
  username: string;
  displayName: string;
  role: string;
  active: boolean;
  sede_assegnata?: 'misterbianco' | 'palermo' | null;
}

const LAST_OPERATOR_KEY = 'coronatek_last_mobile_operator';

export default function MobileOperatorLoginButton({ className = '' }: MobileOperatorLoginButtonProps) {
  const { user } = useAuth();
  const [showOperatorSelect, setShowOperatorSelect] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [selectedOperator, setSelectedOperator] = useState<string | null>(null);

  // WebSocket condiviso — permette di registrare handler per aggiornamenti live
  const { registerHandler } = useSharedWebSocket({
    userId: user?.id ? String(user.id) : '',
    userName: user?.name || user?.email || 'Admin',
    userType: 'desktop',
    enabled: !!user && showOperatorSelect, // connetti solo quando la modale è aperta
  });

  // Subscribe live al broadcast backend `mobile_clients_update`
  useEffect(() => {
    if (!showOperatorSelect) return;
    const unsub = registerHandler('mobile_clients_update', (data: any) => {
      const ids = new Set<string>((data?.mobiles || []).map((m: any) => String(m.userId)));
      setOnlineUserIds(ids);
    });
    return () => { unsub(); };
  }, [showOperatorSelect, registerHandler]);
  const [qrData, setQrData] = useState<{
    loginToken: string;
    qrCodeUrl: string;
    qrCodeDataUrl: string;
    qrUrl?: string;
    expiresAt: string;
    expiresIn: number;
    operator?: { username: string; displayName: string; role: string };
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sedeFilter, setSedeFilter] = useState<'misterbianco' | 'palermo'>('misterbianco');

  // Carica ultimo operatore usato da localStorage
  useEffect(() => {
    const lastOperator = localStorage.getItem(LAST_OPERATOR_KEY);
    if (lastOperator) {
      setSelectedOperator(lastOperator);
    }
  }, []);

  const handleOpenOperatorSelect = async () => {
    if (!user?.id) {
      setError('Utente non autenticato');
      return;
    }

    // Carica lista operatori attivi
    setLoadingOperators(true);
    setError(null);

    try {
      const response = await fetch('/api/mobile-operators/admin/operators?active=true');
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Errore caricamento operatori');
      }

      setOperators(data.operators.filter((op: Operator) => op.active));
      setShowOperatorSelect(true);
      setLoadingOperators(false);

      // Carica in parallelo lo stato online dei mobile client
      fetch('/api/debug/photo-clients')
        .then(r => r.json())
        .then(d => {
          const ids = new Set<string>((d?.mobiles || []).map((m: any) => String(m.userId)));
          setOnlineUserIds(ids);
        })
        .catch(() => { /* ignora, nessuno evidenziato come online */ });
    } catch (err: any) {
      console.error('[QR Login] Errore caricamento operatori:', err);
      setError(err.message || 'Errore di connessione');
      setLoadingOperators(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!user?.id) {
      setError('Utente non autenticato');
      return;
    }

    if (!selectedOperator) {
      setError('Seleziona un operatore');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/mobile-operators/auth/generate-login-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          operatorUsername: selectedOperator,
          desktopUserId: user.id,
          qrUrlBase: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Errore generazione QR');
      }

      // Salva ultimo operatore usato
      localStorage.setItem(LAST_OPERATOR_KEY, selectedOperator);

      setQrData({
        ...data,
        qrCodeDataUrl: data.qrCodeDataUrl || data.qrDataUrl,
        loginToken: data.loginToken || data.token || data.qrToken,
      });
      setShowOperatorSelect(false);
      setShowQRModal(true);
      setLoading(false);
    } catch (err: any) {
      console.error('[QR Login] Errore generazione:', err);
      setError(err.message || 'Errore di connessione');
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'officina': return 'bg-green-100 text-green-700';
      case 'accettazione': return 'bg-blue-100 text-blue-700';
      case 'magazzino': return 'bg-amber-100 text-amber-700';
      case 'admin': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      officina: 'Officina',
      accettazione: 'Accettazione',
      magazzino: 'Magazzino',
      admin: 'Admin'
    };
    return labels[role] || role;
  };

  return (
    <>
      {/* Pulsante per aprire selezione operatore */}
      <button
        onClick={handleOpenOperatorSelect}
        disabled={loadingOperators}
        className={`flex flex-col items-center justify-center gap-0.5 px-3 rounded-lg cursor-pointer transition-all duration-300 hover:scale-[1.15] hover:shadow-2xl hover:shadow-purple-500/30 hover:z-50 backdrop-blur-sm h-[48px] relative disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        style={{
          background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 50%, #6b21a8 100%)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
        title="Genera QR code per login operatori mobile"
      >
        {loadingOperators ? (
          <Loader2 className="w-5 h-5 text-purple-200 animate-spin" />
        ) : (
          <QrCode className="w-5 h-5 text-purple-200" />
        )}
        <span className="text-[10px] text-white font-medium leading-tight text-center">
          Login
        </span>
        <span className="text-[10px] text-white font-medium leading-tight text-center">
          Operatori
        </span>
        <span className="text-[10px] text-white font-medium leading-tight text-center">
          Smartphone
        </span>
      </button>

      {/* Modale Selezione Operatore */}
      {showOperatorSelect && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Seleziona Operatore
                </h2>
                <button
                  onClick={() => setShowOperatorSelect(false)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Toggle Sede */}
              <div className="flex items-center justify-center gap-2 bg-white/10 rounded-lg p-1">
                <button
                  onClick={() => setSedeFilter('misterbianco')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    sedeFilter === 'misterbianco'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  🔵 Misterbianco
                </button>
                <button
                  onClick={() => setSedeFilter('palermo')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    sedeFilter === 'palermo'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  🟠 Palermo
                </button>
              </div>
            </div>

            {/* Lista Operatori */}
            <div className="flex-1 overflow-y-auto p-4">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                {operators
                  // Chi non ha una sede assegnata resta VISIBILE in entrambe le schede:
                  // un filtro che nasconde in silenzio e' come un elenco vuoto senza
                  // spiegazione, ed e' esattamente il difetto che si stava correggendo.
                  .filter((op) => !op.sede_assegnata || op.sede_assegnata === sedeFilter)
                  .map((op) => {
                    const isLastUsed = op.username === localStorage.getItem(LAST_OPERATOR_KEY);
                    const isOnline = onlineUserIds.has(String(op.id));

                    return (
                      <button
                        key={op.id}
                        onClick={() => setSelectedOperator(op.username)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          selectedOperator === op.username
                            ? 'border-purple-500 bg-purple-50'
                            : isOnline
                              ? 'border-green-300 hover:border-green-500 hover:bg-green-50'
                              : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-600" />
                            <span className="font-semibold text-gray-900">{op.displayName}</span>
                            {isOnline ? (
                              <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full border border-green-300">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                <Wifi className="w-3 h-3" />
                                Online
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                <WifiOff className="w-3 h-3" />
                                Offline
                              </span>
                            )}
                            {isLastUsed && (
                              <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                                <Clock className="w-3 h-3" />
                                Ultimo usato
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              op.sede_assegnata === 'misterbianco'
                                ? 'bg-blue-100 text-blue-700'
                                : op.sede_assegnata === 'palermo'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-gray-100 text-gray-500'
                            }`}>
                              {op.sede_assegnata === 'misterbianco' ? '🔵 MB' : op.sede_assegnata === 'palermo' ? '🟠 PA' : '—'}
                            </span>
                            <span className="text-xs font-mono text-gray-500">{op.username}</span>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(op.role)}`}>
                          {getRoleLabel(op.role)}
                        </span>
                      </button>
                    );
                  })}
              </div>

              {operators.filter((op) => !op.sede_assegnata || op.sede_assegnata === sedeFilter).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Nessun operatore attivo per {sedeFilter === 'misterbianco' ? '🔵 Misterbianco' : '🟠 Palermo'}</p>
                </div>
              )}
            </div>

            {/* Footer con pulsante Genera QR */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleGenerateQR}
                disabled={!selectedOperator || loading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generazione QR...
                  </>
                ) : (
                  <>
                    <QrCode className="w-5 h-5" />
                    Genera QR Code
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale QR Code */}
      {showQRModal && qrData && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <QrCode className="w-6 h-6 text-purple-600" />
                QR Login Operatore
              </h2>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Operatore selezionato */}
            {qrData.operator && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-700 mb-1">QR generato per:</p>
                <p className="text-lg font-bold text-purple-900">{qrData.operator.displayName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-mono text-purple-600">{qrData.operator.username}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(qrData.operator.role)}`}>
                    {getRoleLabel(qrData.operator.role)}
                  </span>
                </div>
              </div>
            )}

            {/* QR Code gigante */}
            <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
              {/* QR disegnato come VETTORE, non come immagine.
                  Il backend manda un PNG da 420px: mostrarlo a 460 lo INGRANDISCE, e
                  l'interpolazione ammorbidisce i bordi dei moduli. Misurato sul campo:
                  un vettore nitido a 400px si legge, il PNG stirato a 460 no.
                  Il PNG resta come ripiego se l'URL non arrivasse (2026-08-17). */}
              {qrData.qrUrl ? (
                <QRCodeSVG value={qrData.qrUrl} size={560} level="H" includeMargin />
              ) : (
                <img
                  src={qrData.qrCodeDataUrl}
                  alt="QR Code Login"
                  className="w-full max-w-[560px] h-auto shadow-xl rounded-lg"
                />
              )}
            </div>

            {/* Istruzioni */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Istruzioni:</strong>
              </p>
              <ol className="text-sm text-blue-700 mt-2 space-y-1 ml-4 list-decimal">
                <li>Apri la webapp mobile sul telefono</li>
                <li>Click su "Scansiona QR Code"</li>
                <li>Inquadra questo QR code</li>
                <li>Inserisci il PIN personale (4 cifre)</li>
              </ol>
            </div>

            {/* Timer scadenza */}
            <div className="mt-4 text-center text-sm text-gray-600">
              ⏱ QR Code valido per <strong>{Math.floor(qrData.expiresIn / 60)} minuti</strong>
              <br />
              <span className="text-xs text-gray-500">Uso singolo per motivi di sicurezza</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
